import { store } from "@/stores";
import axios from "axios";
export const apiUrl = import.meta.env.VITE_API_URL;
const instance = axios.create({ baseURL: apiUrl });
instance.interceptors.request.use(
  (config) => {
    // Get auth data from Redux store
    const authState = store.getState().auth; // Assuming auth is your auth slice name
    
    const token = authState?.token;
    const role = authState?.role;
    const imo_number = authState?.imo_number || authState?.user?.imo_number;
    
    // Add Authorization header if token exists
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    
    // Add imo_number header if exists
    if (imo_number) {
      config.headers["imo_number"] = imo_number;
    }
    
    // Fix the role condition (was using && instead of ||)
    if (role === "BRIDGE" || role === "ENGINE_ROOM") {
      config.headers["x-user-role"] = "USER";
    } else if (role) {
      // Add the actual role for other role types
      config.headers["x-user-role"] = role;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
instance.interceptors.response.use(
  (response) => {
    // Centralized success handling
    if (response?.status) {
      cacheResponse(response.config, response.data);
      return response.data;
    } else {
      return Promise.reject(response?.data?.error || "Unknown error occurred.");
    }
  },
  (error) => {
    const errorMessage =
      error?.response?.data?.error || error.message || "Something went wrong.";
 
    if (
      error?.response?.status ===  403 &&
      errorMessage == "invalid_token"
    ) {
       localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("ship_id");
      localStorage.removeItem("reset_new");
      localStorage.removeItem("role");
      window.location.href = "/login";
    }
    if (
      error?.response?.status === 401 &&
      error?.response?.data?.error?.toLowerCase().includes("jwt")
    ) {
      // Optional: clear storage
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("ship_id");
      localStorage.removeItem("reset_new");
      localStorage.removeItem("role");

      window.location.href = "/login";
      return Promise.reject("Session expired. Redirecting to login.");
    }

    // If no internet, check for cached response
    if (!navigator.onLine) {
      const cachedResponse = getCachedResponse(error.config);
      if (cachedResponse) {
         return cachedResponse;
      }
    }
    console.error("API Error:", error?.response?.data);

    return { success: false, error: errorMessage, data: error?.response?.data };
  }
);

const cacheResponse = async (config, data) => {
  if (config.method === "get" && config.url) {
    const cacheKey = getCacheKey(config);

    // Open the cache storage
    const cache = await caches.open("api-cache");

    // Create a fake request and response to store the data
    const response = new Response(JSON.stringify(data), {
      headers: { "Content-Type": "application/json" },
    });

    // Store the response in the cache
    await cache.put(cacheKey, response);
  }
};

const getCachedResponse = async (config) => {
  const cacheKey = getCacheKey(config);

  // Open the cache storage
  const cache = await caches.open("api-cache");

  // Try to retrieve the response from the cache
  const cachedResponse = await cache.match(cacheKey);

  if (cachedResponse) {
    // Parse and return the JSON data from the cached response
    return cachedResponse.json();
  }

  return null;
};

// Generate a unique cache key based on request URL and params
const getCacheKey = (config) => {
  const url = new URL(config.url, window.location.origin);
  if (config.params) {
    Object.keys(config.params).forEach((key) =>
      url.searchParams.append(key, config.params[key])
    );
  }
  return url.toString();
};

export default instance;
